import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  DollarSign,
  TrendingUp,
  BarChart3,
  FileText,
  MessageCircle,
  MessageSquare,
  Terminal,
  Shield,
  Database,
  Lock,
  Zap,
  RefreshCw
} from 'lucide-react';
import Navbar from './Navbar';

const FinanceIntelligence = () => {
  const navigate = useNavigate();
  const [showStickyButton, setShowStickyButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      const scrollPercentage = scrollPosition / (documentHeight - windowHeight);
      setShowStickyButton(scrollPercentage > 0.5);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    navigate('/?subject=Financial%20Intelligence%20Inquiry', { replace: true });
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Real-Time Margin Visibility',
      description: 'Instant visibility into profit margins across products, services, and customer segments—no spreadsheets required.'
    },
    {
      icon: RefreshCw,
      title: 'Automated Reconciliation',
      description: 'Automatically match transactions across Square, QuickBooks, and bank accounts—eliminating hours of manual work.'
    },
    {
      icon: MessageCircle,
      title: 'Chat With Your Ledger',
      description: 'Ask natural language questions like "What\'s my highest-margin product this quarter?" and get instant answers.'
    },
    {
      icon: FileText,
      title: 'Contextual Fact Sheets',
      description: 'AI-synthesized financial reports that answer complex questions by connecting multiple data sources.'
    },
    {
      icon: Lock,
      title: 'Privacy-First Architecture',
      description: 'Your financial data stays under your control. Read-only API access with zero data retention. Process with your chosen AI model (self-hosted or cloud-based under your agreement).'
    },
    {
      icon: Zap,
      title: 'Ready to Deploy',
      description: 'Pre-engineered workflow that can be deployed in days, not months—proven in production environments.'
    }
  ];

  const integrations = [
    {
      name: 'Square',
      features: [
        'Transaction history and reconciliation',
        'Product-level sales data',
        'Customer purchase patterns',
        'Payment processing details'
      ]
    },
    {
      name: 'QuickBooks',
      features: [
        'Chart of accounts integration',
        'Expense categorization',
        'Invoice and bill tracking',
        'P&L and balance sheet data'
      ]
    },
    {
      name: 'Banking APIs',
      features: [
        'Real-time account balances',
        'Transaction categorization',
        'Cash flow monitoring',
        'Multi-account aggregation'
      ]
    }
  ];

  const useCases = [
    {
      question: 'What\'s my actual profit margin on Product X?',
      answer: 'Financial Intelligence connects Square sales data with QuickBooks COGS and expenses to calculate true margins—including payment processing fees, shipping, and overhead allocation.'
    },
    {
      question: 'Which customers are most profitable?',
      answer: 'By synthesizing transaction history, return rates, and support costs, the system identifies high-value customers and suggests retention strategies.'
    },
    {
      question: 'Why is my bank balance different from QuickBooks?',
      answer: 'The system automatically identifies unreconciled transactions, pending deposits, and timing differences—providing a clear audit trail.'
    },
    {
      question: 'What are my top 5 expense categories this month?',
      answer: 'Natural language queries return categorized expense reports with comparisons to previous periods and budget targets.'
    }
  ];

  const deploymentProcess = [
    {
      step: '01',
      title: 'API Discovery',
      description: 'We audit your Square, QuickBooks, and banking APIs with read-only access to map data structures.'
    },
    {
      step: '02',
      title: 'Workflow Configuration',
      description: 'Pre-built context workflows are customized for your chart of accounts and business logic.'
    },
    {
      step: '03',
      title: 'AI Model Configuration',
      description: 'Your chosen AI model (Ollama, Claude, GPT, or Gemini) is configured with financial synthesis prompts and connected to your workflows.'
    },
    {
      step: '04',
      title: 'Testing & Launch',
      description: 'We validate accuracy with historical data and train your team on natural language queries.'
    }
  ];

  return (
    <>
      <Navbar />

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary-50 to-white pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-semibold mb-8 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Home
            </button>

            <div className="text-center max-w-4xl mx-auto">
              <div className="bg-primary-100 w-24 h-24 rounded-2xl flex items-center justify-center mx-auto mb-8">
                <DollarSign className="h-12 w-12 text-primary-600" />
              </div>

              <div className="inline-block bg-primary-600 text-white text-sm font-bold px-4 py-2 rounded-full mb-6">
                READY TO DEPLOY
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                Financial Intelligence
              </h1>

              <p className="text-2xl text-primary-600 font-medium mb-6">
                Your Ledger. Always in Context.
              </p>

              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Automated financial synthesis connecting Sales, Accounting, and Banking data.
                Get real-time margin visibility, automated reconciliation, and "chat-with-your-ledger"
                capabilities—without surrendering your data.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={scrollToContact}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Request Implementation Audit
                </button>
              </div>

              <div className="mt-8 flex items-center justify-center gap-2 text-sm text-gray-500">
                <Lock className="h-4 w-4" />
                <span>Read-only API access · Zero data retention · Your AI choice</span>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Grid */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Financial Intelligence?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Stop wrestling with spreadsheets. Get instant answers to complex financial questions.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <div
                    key={index}
                    className="bg-white p-8 rounded-2xl border border-gray-100 hover:border-primary-200 hover:shadow-xl transition-all duration-300"
                  >
                    <div className="bg-primary-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="h-6 w-6 text-primary-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Integrations Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                What We Connect
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Pre-engineered integrations for the most common financial data sources.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {integrations.map((integration, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-primary-50 to-white p-8 rounded-2xl border border-primary-100"
                >
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    {integration.name}
                  </h3>
                  <ul className="space-y-3">
                    {integration.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Real Questions. Instant Answers.
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                See how natural language queries unlock insights hidden across your financial systems.
              </p>
            </div>

            <div className="space-y-6">
              {useCases.map((useCase, index) => (
                <div
                  key={index}
                  className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-primary-600 text-white text-sm font-bold px-3 py-1 rounded-full flex-shrink-0 mt-1">
                      Q
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">
                        {useCase.question}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        <span className="font-semibold text-primary-600">Answer: </span>
                        {useCase.answer}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Interactive Demonstration Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                See It In Action
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Watch how your AI reads real business data before answering. No hallucinations.
                No guesses. Just facts from your APIs, synthesized into answers you can trust.
              </p>
            </div>

            {/* Three-Step Visualization */}
            <div className="grid lg:grid-cols-3 gap-8 mb-12">
              {/* Step 1: Question */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-primary-100 rounded-lg">
                    <MessageSquare className="h-5 w-5 text-primary-600" />
                  </div>
                  <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                    Step 1: Question
                  </span>
                </div>
                <div className="bg-primary-50 border-2 border-primary-200 p-6 rounded-2xl shadow-md min-h-[200px] flex items-center">
                  <p className="text-lg font-medium text-primary-900">
                    "What were our labor costs vs. revenue last Thursday?"
                  </p>
                </div>
              </div>

              {/* Step 2: Context FactSheet */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-primary-100 rounded-lg">
                    <Terminal className="h-5 w-5 text-primary-600" />
                  </div>
                  <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                    Step 2: Context Retrieval
                  </span>
                </div>
                <div className="bg-gray-900 border-2 border-gray-700 p-6 rounded-2xl shadow-xl min-h-[200px] overflow-auto">
                  <div className="font-mono text-sm space-y-1">
                    <div className="text-green-400 font-semibold">// Fetching Square Team Schedules API</div>
                    <div className="text-blue-400">GET /v2/labor/shifts?start=2024-01-11</div>
                    <div className="text-yellow-400">Response: &#123;</div>
                    <div className="text-gray-300">  shifts: [</div>
                    <div className="text-gray-300">    &#123; employee: 'Tommy', hours: 9, rate: 45 &#125;,</div>
                    <div className="text-gray-300">    &#123; employee: 'Denise', hours: 8, rate: 50 &#125;,</div>
                    <div className="text-gray-300">    &#123; employee: 'Mike', hours: 7, rate: 38 &#125;</div>
                    <div className="text-gray-300">  ]</div>
                    <div className="text-gray-300">&#125;</div>
                    <div>&nbsp;</div>
                    <div className="text-green-400 font-semibold">// Fetching QuickBooks Revenue Data</div>
                    <div className="text-blue-400">GET /v3/reports/ProfitAndLoss?date=2024-01-11</div>
                    <div className="text-yellow-400">Response: &#123;</div>
                    <div className="text-gray-300">  total_revenue: 3240.00,</div>
                    <div className="text-gray-300">  transactions: 18</div>
                    <div className="text-gray-300">&#125;</div>
                  </div>
                </div>
              </div>

              {/* Step 3: Answer */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-green-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                    Step 3: Answer
                  </span>
                </div>
                <div className="bg-white border-2 border-primary-200 p-6 rounded-2xl shadow-lg min-h-[200px] flex items-center">
                  <p className="text-lg text-gray-800 leading-relaxed">
                    Labor was $1,071 against $3,240 in revenue - a 33.1% labor ratio. This exceeds your target range of 25-30%. Consider adjusting staffing for Thursday shifts.
                  </p>
                </div>
              </div>
            </div>

            {/* Privacy Pillars */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                Your Data Stays Yours
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="flex items-start space-x-4 p-4 bg-white rounded-xl border border-green-200">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg">
                      <Shield className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      Read-Only API Access
                    </h4>
                    <p className="text-sm text-gray-600">
                      No data modified
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-4 bg-white rounded-xl border border-green-200">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg">
                      <Database className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      Zero Data Retention
                    </h4>
                    <p className="text-sm text-gray-600">
                      No data stored by GSCS
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-4 bg-white rounded-xl border border-green-200">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg">
                      <Lock className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      Private AI Model
                    </h4>
                    <p className="text-sm text-gray-600">
                      No data used for training
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Deployment Process */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Deployment Process
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From audit to launch in 2-4 weeks—depending on data complexity.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {deploymentProcess.map((process, index) => (
                <div key={index} className="text-center">
                  <div className="bg-primary-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    {process.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {process.title}
                  </h3>
                  <p className="text-gray-600">
                    {process.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Privacy Guarantee */}
        <section className="py-20 bg-gradient-to-br from-primary-50 to-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Lock className="h-16 w-16 text-primary-600 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Your Data. Your AI. Your Control.
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              We never store, export, or transmit your financial data. All API connections are read-only.
              Your chosen AI model (self-hosted or cloud-based under your API keys) processes queries under your control.
              Whether you utilize industry leaders like ChatGPT and Claude or private, local models for maximum security,
              our system remains the same: We build the pipes; you hold the keys.
              No data sent to us. No third-party access to your financial information.
              You own the intelligence layer—we just build it.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary-600" />
                <span>Read-Only APIs</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary-600" />
                <span>Zero Data Retention</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary-600" />
                <span>Client-Controlled AI</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary-600" />
                <span>Complete Ownership</span>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to Deploy Your Financial Intelligence?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Let's audit your financial data sources and show you what's possible.
            </p>
            <button
              onClick={scrollToContact}
              className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Request Implementation Audit
            </button>
          </div>
        </section>

        {/* Sticky CTA Button */}
        {showStickyButton && (
          <button
            onClick={scrollToContact}
            className="fixed bottom-8 right-8 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-full font-semibold shadow-2xl hover:shadow-3xl transition-all duration-300 animate-pulse z-50"
          >
            Get Started →
          </button>
        )}
      </div>
    </>
  );
};

export default FinanceIntelligence;
